import giftBoxQueries from '@apis/queries/giftBoxQueries';
import BlankEtc from '@components/blank/BlankEtc';
import Description from '@components/Description';
import Header from '@components/Header';
import Row from '@components/Row';
import Skeleton from '@components/Skeleton';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import InfoIcon from '@icons/InfoIcon';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Params {
    giftCouponId: string;
}

declare module '@stackflow/config' {
    interface Register {
        GiftUsageActivity: Params;
    }
}

const GiftUsageActivity: ActivityComponentType<'GiftUsageActivity'> = ({ params }) => {
    const { giftCouponId } = params;
    const { t } = useTranslation('gift');
    const scrollRef = useRef<HTMLDivElement>(null);
    const rangeStart = dayjs().subtract(1, 'year').format('YY.MM.DD');
    const rangeEnd = dayjs().format('YY.MM.DD');

    const { data, isPending, isError } = useQuery(giftBoxQueries.usage(giftCouponId));
    const isEmpty = !isPending && !isError && (!data || data.length === 0);

    const scrollToTop = () => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <AppScreen>
            <Header title={t('giftReceived.usage.title')} />
            <ScrollArea ref={scrollRef}>
                <Description>
                    <Row fullWidth spacing={1.5} align='center'>
                        <InfoIcon size={12} variant='fill' />
                        <Text size='body-long' fontWeight='regular' color={colors.textSecondary}>
                            {t('giftReceived.usage.notice')}
                        </Text>
                    </Row>
                </Description>
                <Range>
                    <Text size='body-short' color={colors.textSecondary} fontWeight='regular'>
                        {t('giftReceived.usage.range', { start: rangeStart, end: rangeEnd })}
                    </Text>
                </Range>
                {isPending && (
                    <List aria-busy='true'>
                        {Array.from({ length: 5 }, (_, index) => (
                            <Item key={index}>
                                <Top>
                                    <Skeleton width='50%' height={22} />
                                    <Skeleton width={72} height={22} />
                                </Top>
                                <Bottom>
                                    <Skeleton width={64} height={20} />
                                    <Skeleton width={56} height={20} />
                                </Bottom>
                            </Item>
                        ))}
                    </List>
                )}
                {isError && (
                    <EmptyText color={colors.gray6} align='center'>{t('giftReceived.usage.loadError')}</EmptyText>
                )}
                {isEmpty && <BlankEtc>
                    <Text size='body-short' color={colors.textSecondary} fontWeight='regular' align='center'>
                        {t('giftReceived.usage.emptySubtitle')}<br />{t('giftReceived.usage.empty')}
                    </Text>
                </BlankEtc>}
                {!isPending && !isError && !!data?.length && (
                    <List>
                        {data.map((row) => {
                            const isUsed = row.status === 'USED';
                            return (
                                <Item key={`${row.transactedAt}-${row.shopName}-${row.amount}`}>
                                    <Top>
                                        <Text size='title' color={colors.textSecondary} fontWeight='medium'>
                                            {isUsed
                                                ? t('giftReceived.usage.usedAtShop', { shop: row.shopName })
                                                : t('giftReceived.usage.canceledAtShop', { shop: row.shopName })}
                                        </Text>
                                        <Text size='title' color={isUsed ? colors.textError : undefined} fontWeight='bold'>
                                            {isUsed ? '-' : '+'}
                                            {Math.abs(row.amount).toLocaleString()}
                                            {t('item.priceUnit')}
                                        </Text>
                                    </Top>
                                    <Bottom>
                                        <Text size='body-short' color={colors.textThird}>{dayjs(row.transactedAt).format('YY.MM.DD')}</Text>
                                        {row.balance != null && (
                                            <Text as='span' color={colors.gray6}>
                                                {row.balance.toLocaleString()}{t('item.priceUnit')}
                                            </Text>
                                        )}
                                    </Bottom>
                                </Item>
                            );
                        })}
                    </List>
                )}
            </ScrollArea>
            <Fab type='button' aria-label={t('giftReceived.usage.scrollTop')} onClick={scrollToTop}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 16L12 7L3 16" stroke={colors.gray7} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </Fab>
        </AppScreen>
    );
};

const ScrollArea = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: var(--spacing-4) var(--spacing-5) var(--spacing-20);
    background: ${colors.white};
`;

const Range = styled.div`
    margin: var(--spacing-4) 0;
`;

const List = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
`;

const Item = styled.li`
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1-5);
    padding: var(--spacing-4) 0;
    border-bottom: 1px solid ${colors.gray2};
`;

const Top = styled.div`
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-3);
    align-items: flex-start;
`;

const Bottom = styled.div`
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-3);
`;

const EmptyText = styled(Text)`
    margin: var(--spacing-12) 0;
`;

const Fab = styled.button`
    position: fixed;
    right: 16px;
    bottom: calc(32px + var(--safe-area-bottom));
    display: flex;
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 12px;
    background: ${colors.white};
    box-shadow: 0 6px 10px 0 rgba(0, 0, 0, 0.08);
    cursor: pointer;
`;

export default GiftUsageActivity;
